import { getApiBaseUrl } from '../config/constants';
import type { RemoteLanguage } from '../i18n/languages';
import { apiService } from './api';

export type TranslationStatus = 'ai_draft' | 'draft' | 'approved';

export interface ServerTranslation {
  key: string;
  text: string;
  status: TranslationStatus;
  source_hash: string;
  updated_by?: string;
  updated_at?: string;
  approved_by?: string;
  approved_at?: string;
}

export interface AdminLanguage extends RemoteLanguage {
  admins?: string[];
  created_at?: string;
  updated_at?: string;
  ready_by?: string;
  ready_at?: string;
  ready_summary?: Record<string, number> | null;
  published_by?: string;
  published_at?: string;
}

export interface TranslationSaveItem {
  key: string;
  text: string;
  status: TranslationStatus;
  source_hash: string;
}

/** A saved translation of a long-form document (see i18n/documents.ts). */
export interface ServerDocument {
  id: string;
  status: 'draft' | 'approved';
  source_hash: string;
  text?: string;
  updated_by?: string;
  updated_at?: string;
  approved_by?: string;
  approved_at?: string;
}

export type LanguageStatusAction = 'mark_ready' | 'reopen' | 'publish' | 'unpublish';

/** Pull the server's error message out of "API request failed: 400 {json}". */
export function i18nErrorMessage(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  const jsonStart = raw.indexOf('{');
  if (jsonStart >= 0) {
    try {
      const data = JSON.parse(raw.slice(jsonStart));
      if (data.error) return String(data.error);
      if (data.detail) return String(data.detail);
    } catch {
      /* fall through */
    }
  }
  return raw;
}

// ---- Public (no auth) ----

export async function fetchPublicLanguages(): Promise<RemoteLanguage[] | null> {
  try {
    const res = await fetch(`${getApiBaseUrl()}/i18n/languages`);
    if (!res.ok) return null;
    const data = await res.json();
    return Array.isArray(data.languages) ? data.languages : [];
  } catch {
    return null;
  }
}

export async function fetchCatalog(code: string): Promise<Record<string, string> | null> {
  try {
    const res = await fetch(`${getApiBaseUrl()}/i18n/catalog/${encodeURIComponent(code)}`);
    if (!res.ok) return null;
    const data = await res.json();
    return data.messages && typeof data.messages === 'object' ? data.messages : {};
  } catch {
    return null;
  }
}

/** Saved translation of one document, or null when none is saved (or the request fails). */
export async function fetchSavedDocument(code: string, docId: string): Promise<string | null> {
  try {
    const res = await fetch(
      `${getApiBaseUrl()}/i18n/doc/${encodeURIComponent(code)}/${encodeURIComponent(docId)}`
    );
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data.text === 'string' && data.text.trim() ? data.text : null;
  } catch {
    return null;
  }
}

// ---- Signed in ----

export interface I18nAccess {
  is_manager: boolean;
  languages: RemoteLanguage[];
}

export async function fetchMyTranslationAccess(): Promise<I18nAccess | null> {
  try {
    return await apiService.adminRequest('/i18n/me');
  } catch {
    return null;
  }
}

export async function listAdminLanguages(): Promise<{ is_manager: boolean; languages: AdminLanguage[] }> {
  return apiService.adminRequest('/i18n/admin/languages');
}

export async function upsertLanguage(
  code: string,
  body: { name: string; native_name: string; fallbacks?: string[]; tier?: number }
): Promise<AdminLanguage> {
  const data = await apiService.adminRequest(`/i18n/admin/languages/${encodeURIComponent(code)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  return data.language;
}

export async function renameLanguage(code: string, newCode: string): Promise<AdminLanguage> {
  const data = await apiService.adminRequest(
    `/i18n/admin/languages/${encodeURIComponent(code)}/rename`,
    { method: 'POST', body: JSON.stringify({ new_code: newCode }) }
  );
  return data.language;
}

export async function setLanguageAdmins(code: string, admins: string[]): Promise<string[]> {
  const data = await apiService.adminRequest(
    `/i18n/admin/languages/${encodeURIComponent(code)}/admins`,
    { method: 'PUT', body: JSON.stringify({ admins }) }
  );
  return data.admins;
}

export async function setLanguageStatus(
  code: string,
  action: LanguageStatusAction,
  summary?: Record<string, number>
): Promise<AdminLanguage> {
  const data = await apiService.adminRequest(
    `/i18n/admin/languages/${encodeURIComponent(code)}/status`,
    { method: 'POST', body: JSON.stringify({ action, summary }) }
  );
  return data.language;
}

export async function listTranslations(code: string): Promise<ServerTranslation[]> {
  const data = await apiService.adminRequest(`/i18n/admin/translations/${encodeURIComponent(code)}`);
  return data.items || [];
}

export async function saveTranslations(
  code: string,
  items: TranslationSaveItem[]
): Promise<ServerTranslation[]> {
  const data = await apiService.adminRequest(`/i18n/admin/translations/${encodeURIComponent(code)}`, {
    method: 'PUT',
    body: JSON.stringify({ items }),
  });
  return data.items || [];
}
export async function listDocuments(code: string): Promise<ServerDocument[]> {
  const data = await apiService.adminRequest(`/i18n/admin/docs/${encodeURIComponent(code)}`);
  return data.documents || [];
}

export async function getDocument(code: string, docId: string): Promise<ServerDocument | null> {
  const data = await apiService.adminRequest(
    `/i18n/admin/docs/${encodeURIComponent(code)}/${encodeURIComponent(docId)}`
  );
  return data.document || null;
}

/** Save a document translation. Empty text clears it (back to the AI draft or English). */
export async function saveDocument(
  code: string,
  docId: string,
  doc: { text: string; status: 'draft' | 'approved'; source_hash: string }
): Promise<ServerDocument | null> {
  const data = await apiService.adminRequest(
    `/i18n/admin/docs/${encodeURIComponent(code)}/${encodeURIComponent(docId)}`,
    { method: 'PUT', body: JSON.stringify(doc) }
  );
  return data.document || null;
}
